/* THE RESOLVER — a key, in a language, against the dictionary.

   208 §20 (the extraction, first and alone — rule 3): `interpolate` and
   `resolve` moved out of src/i18n.tsx, UNCHANGED in what they do, and typed —
   a new file lints clean — so a recording can run the one function every
   `t`, `tIn` and `tImperative` resolves through. The provider, the out-of-tree
   arm and the imperative bridge import them from here. */

/** The language every miss falls back through. */
export const FALLBACK_LANG = "en";

/** The dictionary: language → key → text. */
export type I18nCatalog = Readonly<Record<string, Readonly<Record<string, string | undefined>> | undefined>>;
/** A call's variables: `{placeholder}` values, `count`, `fallback`. */
export type I18nVars = Readonly<Record<string, unknown>> | null | undefined;

// Substitute {placeholder} tokens in a translation string with values from
// a vars object. Missing placeholders render literally — keeps debug
// signal visible.
export function interpolate(str: string, vars: I18nVars): string {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (m, k: string) =>
    Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : m,
  );
}

/* 208 §20 [RULED 16, 2026-09-24] — A PLURAL FOLLOWS ITS LANGUAGE'S OWN RULE,
   read from the platform for whatever language is active (`Intl.PluralRules`:
   zero · one · two · few · many · other), never a rule written per language.
   It was English's, written once for every language — `_one` at 1, `_zero` at
   0 when defined, else `_other` — so French put 0 in the plural where its own
   rule puts it in the singular (« 0 doublon »), and German, next, would have
   been English. The resolver tries `key_<category>`, then `key_other`, then
   `key`, the category being THE LANGUAGE TRIED's — the reader's, then
   English's on the fallback. [ADVISOR] `_zero` stays an explicit form where a
   sentence says « none » in words, tried first for 0 in any language (its
   users: `alerts.days_left` « Today », `emailSearch.rows.parties_field` — at
   0 no list); without it, French's « one » for 0 would print « Demain ». */
const RULES = new Map<string, Intl.PluralRules>();

/** The plural category of `n` in `lang` — the platform's rule for that language. */
export function pluralCategory(lang: string, n: number): Intl.LDMLPluralRule {
  let rules = RULES.get(lang);
  if (!rules) {
    try {
      rules = new Intl.PluralRules(lang);
    } catch {
      rules = new Intl.PluralRules(FALLBACK_LANG);
    }
    RULES.set(lang, rules);
  }
  return rules.select(n);
}

// Resolve a key against a (lang → key → text) catalog with English fallback.
// Pluralization: when `vars.count` is a number, the language's own form
// (above). Always falls back through en, then the bare key, or the
// caller-supplied `vars.fallback` string if both EN and the active language
// miss — never blank.
//
// `vars.fallback` was added in Round 7 after Round 6 unmasked a long-running
// inconsistency: numerous call sites already pass `{ fallback: '…' }` as a
// hint, but the resolver previously ignored the field and returned the raw
// key on miss. Honouring it now means an unset key/lang renders the
// caller's English literal instead of leaking `teamSchedule.undefined`-
// style debug strings into the UI.
export function resolve(catalog: I18nCatalog, lang: string, key: string, vars: I18nVars): string {
  if (!key) return "";
  const count = vars && typeof vars.count === "number" ? vars.count : null;
  const candidatesFor = (lng: string): string[] => {
    if (count === null) return [key];
    const out: string[] = [];
    if (count === 0) out.push(`${key}_zero`);
    const category = pluralCategory(lng, count);
    if (category !== "other") out.push(`${key}_${category}`);
    out.push(`${key}_other`, key);
    return out;
  };
  const tryLang = (lng: string): string | undefined => {
    const c = catalog[lng];
    if (!c) return undefined;
    for (const k of candidatesFor(lng)) {
      const text = c[k];
      if (typeof text === "string") return text;
    }
    return undefined;
  };
  // an empty text in the reader's language falls through to English, as `||` made it
  let hit = tryLang(lang);
  if ((hit === undefined || hit === "") && lang !== FALLBACK_LANG) hit = tryLang(FALLBACK_LANG);
  else if (hit === "") hit = undefined;
  if (hit !== undefined) return interpolate(hit, vars);
  if (vars && typeof vars.fallback === "string") return interpolate(vars.fallback, vars);
  return interpolate(key, vars);
}
